<template>
  <v-dialog
    :value="value"
    @input="updateDialog"
    max-width="1000"
    :retain-focus="false"
    content-class="arena-product-dialog">
    <v-card class="arena-product-card">
      <v-toolbar dark color="primary" class="elevation-2 sticky-header">
        <v-toolbar-title class="font-weight-bold">
          {{ dialogTitle }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn @click="handleCancel" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-6 pb-0 sticky-product-fields">
        <v-form ref="form" lazy-validation>
          <v-row dense>
            <v-col cols="12" md="2">
              <div class="image-upload-wrapper full-width">
                <label
                  class="image-upload-box"
                  :class="{
                    disabled: isBusy || !canEditProduct,
                    'has-image': form.image || imagePreview,
                  }">
                  <input
                    type="file"
                    accept="image/*"
                    :disabled="isBusy || !canEditProduct"
                    @change="onImageSelected" />
                  <v-img
                    v-if="form.image || imagePreview"
                    :src="imagePreview || form.image"
                    width="100%"
                    height="100%"
                    cover
                    class="product-image" />
                  <v-icon v-else color="primary">mdi-image-plus</v-icon>
                </label>
              </div>
            </v-col>
            <v-col cols="12" md="10">
              <v-row dense>
                <v-col cols="12" md="5">
                  <v-text-field
                    v-model="form.name"
                    :label="withRequiredAsterisk($t('generalAttributes.name'))"
                    :rules="[rules.required]"
                    :disabled="!canEditProduct"
                    outlined
                    dense />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="form.category_id"
                    :items="categorySelectItems"
                    item-text="name"
                    item-value="id"
                    :label="
                      withRequiredAsterisk(
                        $t('entities.products.chooseCategory'),
                      )
                    "
                    :rules="[rules.required]"
                    :disabled="!canEditProduct || !categorySelectItems.length"
                    outlined
                    dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="form.taxes"
                    :items="taxes"
                    item-text="invoice_label"
                    item-value="id"
                    :label="
                      withRequiredAsterisk($t('entities.products.chooseTaxes'))
                    "
                    :rules="[rules.required, rules.taxSelection]"
                    :disabled="isBusy || !canEditProduct"
                    outlined
                    dense
                    multiple />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col cols="12" md="3">
                  <v-tooltip top :disabled="!isTrackStockDisabled">
                    <template #activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-switch
                          v-model="form.track_stock"
                          :label="
                            withRequiredAsterisk(
                              $t('entities.products.attributes.trackStock'),
                            )
                          "
                          inset
                          dense
                          :disabled="isTrackStockDisabled || !canEditProduct"
                          class="mt-1" />
                      </span>
                    </template>
                    <span>
                      {{
                        !isAdmin
                          ? $t('entities.products.onlyAdminCanChangeTrackStock')
                          : $t('entities.products.trackStockDisableTooltip')
                      }}
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col cols="12" md="9">
                  <v-textarea
                    v-model="form.description"
                    :label="$t('generalAttributes.description')"
                    rows="1"
                    :disabled="!canEditProduct"
                    outlined
                    dense />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <div ref="variantSection" class="variants-section">
        <h4 class="mb-3 font-weight-bold text-uppercase">
          {{ $t('entities.products.productVariants') }}
          <span class="variant-count">{{ `(${variants?.length})` || '' }}</span>
        </h4>
        <p
          v-if="!variants.length"
          class="add-variants-warning text--secondary mb-0">
          {{ $t('entities.products.addVariantMessage') }}
        </p>
        <div v-else ref="variantList" class="variants-section__variant-list">
          <VariantUpsertCard
            v-for="(variant, index) in variants"
            :key="variant.uid || variant.id || index"
            ref="variantCards"
            ref-in-for
            v-model="variants[index]"
            :rules="rules"
            :disabled="isBusy"
            :can-only-change-stock="!canEditProduct"
            :currencies="currencies"
            :is-coins-category="isCoinsCategory"
            :is-passes-category="isPassesCategory"
            :track-stock="form.track_stock"
            :index="index + 1"
            :is-editing="!!variant.isEditing"
            :can-remove="!isBusy && canEditProduct && !variantHasStock(variant)"
            :is-pull-mode="isPullMode"
            @edit-toggle="toggleVariantEditing(index, $event)"
            @remove="removeVariantCard(index)"
            @change="markVariantDirty(index)"
            @stock-transactions:open="openStockTransactionDialog" />
        </div>
        <div class="variants-section__actions">
          <v-btn
            color="primary"
            small
            :disabled="isBusy || !canEditProduct"
            @click="addVariantCard">
            {{ $t('entities.products.addVariant') }}
          </v-btn>
        </div>
      </div>
      <v-card-actions class="sticky-footer px-6 py-4">
        <v-btn text @click="handleCancel">
          {{ $t('miscellaneous.cancel') }}
        </v-btn>
        <v-spacer />
        <v-tooltip top :disabled="!hasVariantStock">
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                v-if="isEditing"
                color="error"
                text
                :disabled="isDeleteDisabled || !canEditProduct"
                @click="deleteDialogOpen = true">
                {{ $t('miscellaneous.delete') }}
              </v-btn>
            </span>
          </template>
          <span>
            {{ $t('entities.products.deleteDisabledTooltip') }}
          </span>
        </v-tooltip>
        <v-btn
          color="primary"
          :loading="isBusy"
          :disabled="isSubmitDisabled || !canEditProduct"
          @click="submit">
          {{ submitButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <DeleteDialog
      v-model="deleteDialogOpen"
      :loading="deleteLoading"
      :title="$t('entities.products.deleteEntity')"
      @confirm="confirmDelete">
      <p class="mb-0">
        {{ $t('miscellaneous.delete') }}
        {{ form.name || $t('entities.products.entity') }}?
      </p>
    </DeleteDialog>
    <StockTransactionUpsertDialog
      :arena-variants="persistedArenaVariants"
      :is-loading="isStockTransactionSubmitting"
      :is-opened="isStockTransactionDialogOpen"
      :arena-id="arenaId"
      :suppliers="stockTransactionSuppliers"
      :invoices="stockTransactionInvoices"
      :is-editing="false"
      :transaction="stockTransactionDraft"
      :transaction-type="selectedStockTransactionType"
      :server-error="stockTransactionServerError"
      :users="[]"
      :allow-transaction-type-selection="true"
      @dialog:close="closeStockTransactionDialog"
      @stock-transaction:upsert="handleVariantStockTransactionUpsert" />
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import FilesApi from '@/services/misc/FilesApi'
import CurrenciesApi from '@/services/shop/CurrenciesApi'
import Invoice from '@/models/shop/InvoiceV2'
import StockTransaction from '@/models/shop/StockTransaction'
import Supplier from '@/models/shop/Supplier'
import DeleteDialog from './DeleteDialog.vue'
import VariantUpsertCard from './VariantUpsertCard.vue'
import StockTransactionUpsertDialog from '../stock-transactions/upsert-dialog/index.vue'
import isRole from '@/mixins/crud/computed/isRole'

const defaultForm = () => ({
  category_id: null,
  name: '',
  description: null,
  taxes: [],
  image: null,
  sort_order: 1,
  track_stock: true,
})

const defaultVariantPrice = (currency_id = '') => ({
  id: null,
  gross_price: '',
  currency_id: currency_id || '',
})

const defaultVariantStock = () => ({
  available: null,
  waste: null,
})

const createVariantState = (sortOrder = 1, defaultCurrencyId = '') => ({
  uid: `variant-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  name: '',
  description: null,
  sku: '',
  image: null,
  attributes: {},
  pack_type: null,
  active: true,
  sort_order: sortOrder,
  prices: [defaultVariantPrice(defaultCurrencyId)],
  isEditing: false,
  hasPendingChanges: false,
  stock: defaultVariantStock(),
})

export default {
  name: 'ArenaProductUpsertDialog',
  components: {
    DeleteDialog,
    VariantUpsertCard,
    StockTransactionUpsertDialog,
  },
  mixins: [isRole],
  props: {
    value: { type: Boolean, default: false },
    arenaId: { type: [String, Number], default: null },
    categories: { type: Array, default: () => [] },
    product: { type: Object, default: null },
    isSubmitting: { type: Boolean, default: false },
    deleteLoading: { type: Boolean, default: false },
    taxes: { type: Array, default: () => [] },
  },
  data() {
    return {
      imageFile: null,
      form: defaultForm(),
      imagePreview: '',
      deleteDialogOpen: false,
      isFormProcessing: false,
      variants: [],
      deletedVariantIds: [],
      currencies: [],
      isStockTransactionDialogOpen: false,
      isStockTransactionSubmitting: false,
      stockTransactionSuppliers: [],
      stockTransactionInvoices: [],
      stockTransactionServerError: null,
      selectedStockTransactionType: 'ADD',
      stockTransactionDraft: {},
    }
  },
  computed: {
    rules() {
      return {
        required: (value) => {
          if (typeof value === 'string') {
            return value.trim().length > 0 || this.$t('validation.required')
          }
          return value !== null && value !== undefined && value !== ''
            ? true
            : this.$t('validation.required')
        },
        taxSelection: (value) => {
          return Array.isArray(value) && value.length > 0
            ? true
            : this.$t('validation.atLeastOneTax')
        },
      }
    },
    isEditing() {
      return !!(this.product && this.product.id)
    },
    isBusy() {
      return this.isSubmitting || this.isFormProcessing
    },
    defaultCurrencyId() {
      if (!Array.isArray(this.currencies) || !this.currencies.length) {
        return ''
      }
      return this.currencies[0]?.id || ''
    },
    isPullMode() {
      return !!(this.product?.global_product_id && !this.product?.id)
    },
    isCreateModeOnly() {
      return !this.isEditing && !this.isPullMode
    },
    categorySelectItems() {
      const shouldFilterCoinsAndPasses = !this.isAdmin && this.isCreateModeOnly
      if (!shouldFilterCoinsAndPasses || !Array.isArray(this.categories)) {
        return this.categories || []
      }
      return this.categories.filter((c) => c.code !== 'CNS' && c.code !== 'PAS')
    },
    currentlySelectedCategory() {
      if (!this.form.category_id) return null
      return this.categories.find((it) => it.id === this.form.category_id)
    },
    isCoinsCategory() {
      if (!this.currentlySelectedCategory) return false
      return this.currentlySelectedCategory?.code === 'CNS'
    },
    isPassesCategory() {
      if (!this.currentlySelectedCategory) return false
      return this.currentlySelectedCategory?.code === 'PAS'
    },
    isDigitalAsset() {
      return this.isCoinsCategory || this.isPassesCategory
    },
    hasVariantStock() {
      if (!Array.isArray(this.variants) || !this.variants.length) {
        return false
      }
      return this.variants.some((variant) => {
        const stock = this.normalizeVariantStock(variant?.stock)
        return (
          this.normalizeStockValue(stock.available) > 0 ||
          this.normalizeStockValue(stock.waste) > 0
        )
      })
    },
    isTrackStockLocked() {
      return !!this.form.track_stock && this.hasVariantStock
    },
    isTrackStockDisabled() {
      if (!this.isAdmin) return true
      if (!this.form.category_id) return false
      return this.isTrackStockLocked
    },
    canEditProduct() {
      return this.isAdmin || this.isManager
    },
    hasVariants() {
      return Array.isArray(this.variants) && this.variants.length > 0
    },
    persistedArenaVariants() {
      if (!Array.isArray(this.variants)) {
        return []
      }
      return this.variants.filter((variant) => !!variant && !!variant.id)
    },
    isSubmitDisabled() {
      if (this.isBusy) return true
      if (!this.hasVariants) return true
      if (
        this.isPullMode &&
        !this.variants.some((v) => v && v.eligibleForCreation === true)
      ) {
        return true
      }
      return false
    },
    isDeleteDisabled() {
      return this.isBusy || this.hasVariantStock
    },
    dialogTitle() {
      if (this.product?.global_product_id && !this.product?.id) {
        return this.$t('entities.products.sendGlobalProductToArenaProducts')
      }
      return this.isEditing
        ? this.$t('entities.products.editArenaProduct')
        : this.$t('entities.products.createNewArenaProduct')
    },
    submitButtonText() {
      if (this.product?.global_product_id && !this.product?.id) {
        return this.$t('miscellaneous.add')
      }
      return this.isEditing
        ? this.$t('miscellaneous.save')
        : this.$t('miscellaneous.create')
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.onOpen()
      } else {
        this.resetForm()
      }
    },
    product: {
      immediate: true,
      handler(newProduct) {
        if (newProduct) {
          this.form = {
            category_id: newProduct.category_id,
            name: newProduct.name || '',
            description: newProduct.description || null,
            taxes: Array.isArray(newProduct.taxes)
              ? newProduct.taxes.map((it) => it.id)
              : [],
            image: newProduct.image || null,
            sort_order: newProduct.sort_order || 1,
            track_stock: !!newProduct.track_stock,
          }
          this.imagePreview = ''
          this.imageFile = null
        } else {
          this.form = defaultForm()
          this.imagePreview = ''
          this.imageFile = null
        }
        this.initializeVariantsFromProduct(newProduct)
      },
    },
    arenaId(newArenaId) {
      if (newArenaId) {
        if (this.value) {
          this.fetchArenaCurrencies()
        }
      } else if (this.value) {
        this.close()
        this.currencies = []
      }
      this.stockTransactionSuppliers = []
      this.stockTransactionInvoices = []
      if (!newArenaId) {
        this.closeStockTransactionDialog()
      }
    },
    defaultCurrencyId(newCurrencyId) {
      // When currency becomes available, update variant prices that don't have currency_id
      if (newCurrencyId && this.variants.length) {
        this.updateVariantPricesWithCurrency()
      }
    },
    'form.category_id'(newCategoryId) {
      if (!newCategoryId) return
      if (this.isPullMode || this.isEditing) return
      if (this.isDigitalAsset) {
        this.form.track_stock = false
      } else {
        this.form.track_stock = true
      }
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    updateDialog(val) {
      this.$emit('input', val)
    },
    withRequiredAsterisk(label) {
      return `${label} *`
    },
    async onOpen() {
      if (!this.arenaId) {
        this.close()
        return
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
      await this.fetchArenaCurrencies()
    },
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
      this.closeStockTransactionDialog()
      this.form = defaultForm()
      this.imagePreview = ''
      this.imageFile = null
      this.imagePreview = ''
      this.deleteDialogOpen = false
      this.deletedVariantIds = []
      this.clearVariants()
    },
    handleCancel() {
      this.close()
    },
    close() {
      this.updateDialog(false)
      this.deleteDialogOpen = false
      this.closeStockTransactionDialog()
    },
    async submit() {
      const formRef = this.$refs.form
      if (formRef && typeof formRef.validate === 'function') {
        const isValid = formRef.validate()
        if (!isValid) {
          return
        }
      }
      if (!this.arenaId) {
        return
      }
      if (!this.hasVariants) {
        this.notifyVariantsRequired()
        return
      }
      const variantsValid = await this.validateVariantForms()
      if (!variantsValid) {
        return
      }
      const variantPayloads = this.buildVariantPayloads()
      this.isFormProcessing = true
      try {
        if (this.imageFile) {
          const uploaded = await this.uploadSelectedImage()
          if (!uploaded) {
            return
          }
        }
        const payload = this.buildProductPayload()
        this.$emit('product:upsert', {
          ...payload,
          variants: variantPayloads,
          deletedVariantIds: [...this.deletedVariantIds],
        })
      } catch (error) {
        console.error(error)
      } finally {
        this.isFormProcessing = false
      }
    },
    buildProductPayload() {
      const form = this.form
      if (this.product?.global_product_id && !this.product?.id) {
        return this.buildFullProductPayload()
      }
      if (!this.isEditing || !this.product) {
        return this.buildFullProductPayload()
      }
      const product = this.product
      const payload = { id: product.id }
      const nameTrimmed = (form.name || '').trim()
      const nameChanged = nameTrimmed !== (product.name || '').trim()
      if (nameChanged) payload.name = nameTrimmed

      const descForm = form.description || null
      const descOrig = product.description || null
      if (descForm !== descOrig) payload.description = descForm

      if (form.category_id !== product.category_id) {
        payload.category_id = form.category_id
      }
      const origTaxIds = (product.taxes || [])
        .map((t) => t.id)
        .filter((id) => id != null)
        .sort()
      const formTaxIds = (form.taxes || []).slice().sort()
      const taxesEqual =
        origTaxIds.length === formTaxIds.length &&
        origTaxIds.every((id, i) => id === formTaxIds[i])
      if (!taxesEqual) payload.taxes = form.taxes

      const imageForm = form.image || ''
      const imageOrig = product.image || ''
      if (imageForm !== imageOrig) payload.image = imageForm

      const trackForm = !!form.track_stock
      const trackOrig = !!product.track_stock
      if (trackForm !== trackOrig) payload.track_stock = trackForm

      return payload
    },
    buildFullProductPayload() {
      const form = this.form
      const payload = {
        category_id: form.category_id,
        name: form.name.trim(),
        sort_order: 1,
        track_stock: !!form.track_stock,
        taxes: form.taxes,
      }
      if (form.description) payload.description = form.description
      if (form.image) payload.image = form.image
      if (this.product?.global_product_id) {
        payload.global_product_id = this.product.global_product_id
      }
      if (this.isEditing && this.product?.id) payload.id = this.product.id
      return payload
    },
    confirmDelete() {
      if (!this.product || !this.product.id) {
        this.deleteDialogOpen = false
        return
      }
      this.$emit('product:delete', this.product.id)
    },
    async uploadSelectedImage() {
      try {
        const response = await FilesApi.upload('arenas', this.imageFile)
        this.imageFile = null
        if (!response || response.status !== 200 || !response.data?.link) {
          this.notifyUploadError()
          return false
        }
        this.form.image = response.data.link
        this.imagePreview = ''
        return true
      } catch (error) {
        console.error(error)
        this.notifyUploadError()
        return false
      }
    },
    notifyUploadError() {
      this.showSnackbar({
        color: 'error',
        message: this.$t('miscellaneous.fileUploadFailed'),
      })
    },
    notifyVariantsRequired() {
      this.showSnackbar({
        color: 'error',
        message: this.$t('entities.products.addVariantMessage'),
      })
    },
    onImageSelected(event) {
      const [file] = event.target.files || []
      if (!file) {
        this.imageFile = null
        this.imagePreview = ''
        return
      }
      this.imageFile = file
      this.imagePreview = URL.createObjectURL(file)
    },
    addVariantCard() {
      const nextSortOrder = this.variants.length + 1
      const newVariant = {
        ...createVariantState(nextSortOrder, this.defaultCurrencyId),
        isEditing: true,
        hasPendingChanges: true,
        ...(this.isPullMode ? { eligibleForCreation: false } : {}),
      }
      this.variants = [...this.variants, newVariant]
      this.scrollVariantSectionToBottom()
    },
    scrollVariantSectionToBottom() {
      this.$nextTick(() => {
        const sectionRef = this.$refs.variantList || this.$refs.variantSection
        const sectionEl = sectionRef?.$el || sectionRef
        if (!sectionEl) {
          return
        }
        if (typeof sectionEl.scrollTo === 'function') {
          sectionEl.scrollTo({
            top: sectionEl.scrollHeight,
            behavior: 'smooth',
          })
        } else {
          sectionEl.scrollTop = sectionEl.scrollHeight
        }
      })
    },
    removeVariantCard(index) {
      if (index < 0 || index >= this.variants.length) return
      const variant = this.variants[index]
      if (!variant) return
      if (variant.id) {
        this.deletedVariantIds.push(variant.id)
      }
      const next = [...this.variants]
      next.splice(index, 1)
      this.variants = next
      this.recalculateVariantSortOrder()
      this.resetVariantValidation()
    },
    variantHasStock(variant) {
      return (
        Number(variant?.stock?.available) > 0 ||
        Number(variant?.stock?.waste) > 0
      )
    },
    clearVariants() {
      this.variants = []
      this.resetVariantValidation()
    },
    recalculateVariantSortOrder() {
      this.variants = this.variants.map((variant, idx) => ({
        ...variant,
        sort_order: idx + 1,
      }))
    },
    resetVariantValidation() {
      const refs = this.$refs.variantCards
      if (!refs) return
      const cards = Array.isArray(refs) ? refs : [refs]
      cards.forEach((card) => {
        if (card && typeof card.resetValidation === 'function') {
          card.resetValidation()
        }
      })
    },
    async validateVariantForms() {
      const refs = this.$refs.variantCards
      if (!refs) {
        return true
      }
      const cards = Array.isArray(refs) ? refs : [refs]
      if (this.isPullMode) {
        cards.forEach((card, index) => {
          const isEligible =
            this.variants[index] &&
            this.variants[index].eligibleForCreation === true
          if (
            !isEligible &&
            card &&
            typeof card.resetValidation === 'function'
          ) {
            card.resetValidation()
          }
        })
      }
      const validations = cards.map((card, index) => {
        if (
          this.isPullMode &&
          !(
            this.variants[index] &&
            this.variants[index].eligibleForCreation === true
          )
        ) {
          return true
        }
        if (card && typeof card.validate === 'function') {
          return card.validate()
        }
        return true
      })
      // validations may contain booleans or promises depending on Vuetify version
      const results = await Promise.all(validations)
      return results.every((result) => !!result)
    },
    buildVariantPayloads() {
      if (!Array.isArray(this.variants)) {
        return []
      }
      const source = this.isPullMode
        ? this.variants.filter((v) => v && v.eligibleForCreation === true)
        : this.variants
      return source
        .map((variant, index) => {
          if (variant.id && !variant.hasPendingChanges) {
            return null
          }
          const trimmedName = (variant.name || '').trim()
          const trimmedSku = (variant.sku || '').trim()
          if (!trimmedName || !trimmedSku) {
            return null
          }
          if (variant.id && variant._initial) {
            return this.buildVariantPatchPayload(variant, index)
          }
          return this.buildFullVariantPayload(variant, index)
        })
        .filter(Boolean)
    },
    buildVariantPatchPayload(variant, index) {
      const current = this.variantToPayloadShape(variant)
      const initial = variant._initial
      const payload = { id: variant.id }
      if (current.name !== initial.name) payload.name = current.name
      if (current.sku !== initial.sku) payload.sku = current.sku
      if (current.attributes !== initial.attributes) {
        payload.attributes = variant.attributes || {}
      }
      if (current.pack_type !== initial.pack_type) {
        payload.pack_type = current.pack_type
      }
      if (current.active !== initial.active) payload.active = current.active
      const sortOrder =
        typeof variant.sort_order === 'number' ? variant.sort_order : index + 1
      if (current.sort_order !== initial.sort_order) {
        payload.sort_order = sortOrder
      }
      if (current.description !== initial.description) {
        payload.description = current.description
      }
      if (current.image !== initial.image) {
        payload.image = current.image
      }
      if (current.prices !== initial.prices) {
        const normalized = this.normalizeVariantPrices(variant.prices)
        if (normalized.length) payload.prices = normalized
      }
      return Object.keys(payload).length > 1 ? payload : null
    },
    buildFullVariantPayload(variant, index) {
      const trimmedName = (variant.name || '').trim()
      const trimmedSku = (variant.sku || '').trim()
      const payload = {
        name: trimmedName,
        sku: trimmedSku,
        attributes: variant.attributes || {},
        pack_type: variant.pack_type || null,
        active: !!variant.active,
        sort_order:
          typeof variant.sort_order === 'number'
            ? variant.sort_order
            : index + 1,
      }
      if (variant.id) payload.id = variant.id
      if (variant.global_variant_id) {
        payload.global_variant_id = variant.global_variant_id
      }
      const desc = (variant.description || '').trim() || null
      if (desc) payload.description = desc
      const img = (variant.image || '').trim()
      if (img) payload.image = img
      const normalizedPrices = this.normalizeVariantPrices(variant.prices)
      if (normalizedPrices.length) payload.prices = normalizedPrices
      return payload
    },
    normalizeVariantPrices(prices) {
      if (!Array.isArray(prices)) {
        return []
      }
      return prices
        .map((price) => {
          if (!price) return null
          const normalizedString =
            price.gross_price === '' || price.gross_price === null
              ? null
              : String(price.gross_price).replace(',', '.')
          const parsedGross =
            normalizedString === null ? null : Number(normalizedString)
          if (
            parsedGross === null ||
            Number.isNaN(parsedGross) ||
            !price.currency_id
          ) {
            return null
          }
          const normalizedPrice = {
            gross_price: String(parsedGross),
            currency_id: price.currency_id,
          }
          if (price.id) {
            normalizedPrice.id = price.id
          }
          return normalizedPrice
        })
        .filter(Boolean)
    },
    initializeVariantsFromProduct(product) {
      if (
        !product ||
        !Array.isArray(product.variants) ||
        !product.variants.length
      ) {
        this.clearVariants()
        return
      }
      const mapped = product.variants.map((variant, index) =>
        this.createVariantFromExisting(variant, index + 1),
      )
      if (this.isPullMode) {
        mapped.forEach((v) => {
          v.eligibleForCreation = !!v.id
        })
      }
      this.variants = mapped
      this.resetVariantValidation()
      // Ensure currency_id is set for all variant prices after currencies are loaded
      this.updateVariantPricesWithCurrency()
    },
    updateVariantPricesWithCurrency() {
      if (!this.defaultCurrencyId) {
        // If currencies aren't loaded yet, wait for them
        return
      }
      this.variants = this.variants.map((variant) => {
        let next = variant
        if (!Array.isArray(variant.prices) || !variant.prices.length) {
          next = {
            ...variant,
            prices: [defaultVariantPrice(this.defaultCurrencyId)],
          }
        } else {
          next = {
            ...variant,
            prices: variant.prices.map((price) => ({
              ...price,
              currency_id: price.currency_id || this.defaultCurrencyId,
            })),
          }
        }
        if (next.id && !next._initial) {
          next = {
            ...next,
            _initial: this.variantToPayloadShape(next),
          }
        }
        return next
      })
    },
    variantToPayloadShape(variant) {
      const trimmedName = (variant.name || '').trim()
      const trimmedSku = (variant.sku || '').trim()
      const shape = {
        name: trimmedName,
        sku: trimmedSku,
        attributes: JSON.stringify(variant.attributes || {}),
        pack_type: variant.pack_type || null,
        active: !!variant.active,
        sort_order:
          typeof variant.sort_order === 'number' ? variant.sort_order : 1,
        description: (variant.description || '').trim() || null,
        image: (variant.image || '').trim(),
        prices: JSON.stringify(this.normalizeVariantPrices(variant.prices)),
      }
      return shape
    },
    createVariantFromExisting(variant, sortOrder = 1) {
      const base = createVariantState(sortOrder, this.defaultCurrencyId)
      return {
        ...base,
        ...variant,
        uid: `variant-${variant.id || sortOrder}-${Date.now()}`,
        id: variant.id || variant._id || null,
        name: variant.name || '',
        description: variant.description || null,
        sku: variant.sku || '',
        image: variant.image || null,
        active: !!variant.active,
        sort_order: variant.sort_order || sortOrder,
        prices:
          Array.isArray(variant.prices) && variant.prices.length
            ? variant.prices.map((price) => ({
                id: price?.id || null,
                gross_price:
                  price?.gross_price === null ||
                  price?.gross_price === undefined
                    ? ''
                    : price.gross_price,
                currency_id: price?.currency_id || this.defaultCurrencyId,
              }))
            : [defaultVariantPrice(this.defaultCurrencyId)],
        isEditing: false,
        hasPendingChanges: false,
        stock: this.normalizeVariantStock(variant?.stock),
      }
    },
    normalizeVariantStock(stock) {
      if (!stock || typeof stock !== 'object') {
        return defaultVariantStock()
      }
      return {
        ...defaultVariantStock(),
        available:
          stock.available === null || stock.available === undefined
            ? null
            : stock.available,
        waste:
          stock.waste === null || stock.waste === undefined
            ? null
            : stock.waste,
      }
    },
    toggleVariantEditing(index, value) {
      const variant = this.variants[index]
      if (!variant) return
      this.$set(this.variants, index, {
        ...variant,
        isEditing: !!value,
      })
    },
    markVariantDirty(index) {
      const variant = this.variants[index]
      if (!variant) return
      if (variant.hasPendingChanges) return
      this.$set(this.variants, index, {
        ...variant,
        hasPendingChanges: true,
      })
    },
    async openStockTransactionDialog(variant = null) {
      if (!this.arenaId) {
        this.showSnackbar({
          color: 'error',
          message: this.$t('entities.stockTransactions.selectArenaReminder'),
        })
        return
      }
      this.stockTransactionServerError = null
      this.selectedStockTransactionType = 'ADD'
      this.stockTransactionDraft =
        variant && variant.id ? { arena_variant_id: variant.id } : {}
      await this.ensureStockTransactionResources()
      this.isStockTransactionDialogOpen = true
    },
    async ensureStockTransactionResources() {
      const loaders = []
      if (!this.stockTransactionSuppliers.length) {
        loaders.push(this.fetchStockTransactionSuppliers())
      }
      if (!this.stockTransactionInvoices.length) {
        loaders.push(this.fetchStockTransactionInvoices())
      }
      if (loaders.length) {
        await Promise.all(loaders)
      }
    },
    async fetchStockTransactionSuppliers() {
      if (!this.arenaId) {
        this.stockTransactionSuppliers = []
        return
      }
      try {
        const response = await Supplier.api.list(this.arenaId)
        const responseData = response.data?.data
        this.stockTransactionSuppliers = Array.isArray(responseData)
          ? responseData.map((supplier) => new Supplier(supplier))
          : []
      } catch (error) {
        console.error('Failed to fetch arena suppliers', error)
        this.stockTransactionSuppliers = []
      }
    },
    async fetchStockTransactionInvoices() {
      if (!this.arenaId) {
        this.stockTransactionInvoices = []
        return
      }
      try {
        const response = await Invoice.api.getArenaInvoices(this.arenaId)
        const responseData = response.data?.data
        this.stockTransactionInvoices = Array.isArray(responseData)
          ? responseData.map((invoice) => new Invoice(invoice))
          : []
      } catch (error) {
        console.error('Failed to fetch arena invoices', error)
        this.stockTransactionInvoices = []
      }
    },
    closeStockTransactionDialog() {
      this.isStockTransactionDialogOpen = false
      this.stockTransactionServerError = null
      this.selectedStockTransactionType = 'ADD'
      this.stockTransactionDraft = {}
    },
    async handleVariantStockTransactionUpsert(payload, transactionType) {
      if (!this.arenaId) {
        return
      }
      this.isStockTransactionSubmitting = true
      this.stockTransactionServerError = null
      try {
        const response = await StockTransaction.api.createStockTransaction(
          this.arenaId,
          payload,
        )
        this.applyVariantStockAdjustment(
          payload?.arena_variant_id,
          payload?.quantity,
          transactionType,
        )
        this.displayStockTransactionToast(
          payload,
          transactionType,
          response.data,
        )
        this.closeStockTransactionDialog()
      } catch (error) {
        console.error('Failed to create stock transaction', error)
        this.stockTransactionServerError = error
      } finally {
        this.isStockTransactionSubmitting = false
      }
    },
    displayStockTransactionToast(payload, transactionType, createdTransaction) {
      const variant = this.persistedArenaVariants.find(
        (it) => it.id === payload?.arena_variant_id,
      )
      this.showSnackbar({
        color: 'success',
        message: this.$t(
          `entities.stockTransactions.notifications.${transactionType}`,
          {
            quantity: payload?.quantity || 0,
            variantName: variant?.name || '',
            availableStockAfter: createdTransaction.available_stock_after,
          },
        ),
      })
    },
    applyVariantStockAdjustment(variantId, quantity, transactionType) {
      if (!variantId) {
        return
      }
      const parsedQuantity = Number(quantity)
      if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
        return
      }
      const variantIndex = this.variants.findIndex(
        (variant) => variant && variant.id === variantId,
      )
      if (variantIndex < 0) {
        return
      }
      const adjustment = this.getStockAdjustment(
        parsedQuantity,
        transactionType,
      )
      if (!adjustment) {
        return
      }
      const variant = this.variants[variantIndex]
      const currentStock = this.normalizeVariantStock(variant?.stock)
      const nextStock = {
        ...currentStock,
      }
      if (adjustment.available !== undefined) {
        const nextAvailable =
          this.normalizeStockValue(currentStock.available) +
          adjustment.available
        nextStock.available = this.normalizeNonNegativeStock(nextAvailable)
      }
      if (adjustment.waste !== undefined) {
        const nextWaste =
          this.normalizeStockValue(currentStock.waste) + adjustment.waste
        nextStock.waste = this.normalizeNonNegativeStock(nextWaste)
      }
      this.$set(this.variants, variantIndex, {
        ...variant,
        stock: nextStock,
      })
    },
    getStockAdjustment(quantity, transactionType) {
      const map = {
        ADD: { available: quantity },
        REMOVE: { available: -quantity },
        WASTE: { available: -quantity, waste: quantity },
        RESOLVE_WASTE: { available: quantity, waste: -quantity },
      }
      return map[transactionType] || null
    },
    normalizeStockValue(value) {
      if (value === null || value === undefined) {
        return 0
      }
      if (typeof value === 'number' && !Number.isNaN(value)) {
        return value
      }
      const parsed = Number(value)
      return Number.isNaN(parsed) ? 0 : parsed
    },
    normalizeNonNegativeStock(value) {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return 0
      }
      return value < 0 ? 0 : value
    },
    async fetchArenaCurrencies() {
      if (!this.arenaId) {
        this.currencies = []
        return
      }
      try {
        const response = await CurrenciesApi.getArenaCurrencies(this.arenaId)
        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.data || []
        this.currencies = Array.isArray(payload) ? payload : []
        // Update variant prices with currency_id after currencies are loaded
        if (this.variants.length) {
          this.updateVariantPricesWithCurrency()
        }
      } catch (error) {
        console.error(error)
        this.currencies = []
      }
    },
  },
}
</script>

<style scoped>
.arena-product-dialog {
  max-height: 1450px;
}

.arena-product-card {
  display: flex;
  flex-direction: column;
  max-height: 1450px;
  --variant-section-padding: 0.5rem;
  --variant-section-background-color: #f5f5f5;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 5;
}

.sticky-product-fields {
  position: sticky;
  top: 64px;
  z-index: 4;
  background: white;
}

.sticky-footer {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: white;
}

.variants-section {
  border-radius: 4px;
  margin: 0 1rem;
  padding: var(--variant-section-padding) var(--variant-section-padding) 0;
  background-color: var(--variant-section-background-color);
}

.add-variants-warning {
  padding: 0.5rem 0 0.75rem;
  text-align: center;
}

.variants-section__actions {
  display: flex;
  justify-content: center;
  margin: 0 1rem;
  padding: var(--variant-section-padding) 0;
  background-color: var(--variant-section-background-color);
}

.variants-section__variant-list {
  max-height: 30rem;
  overflow-y: auto;
}

.variants-section__variant-list ::v-deep .variant-card:last-child {
  margin-bottom: 0 !important;
}

.variant-count {
  margin-left: 0.25rem;
}

.image-upload-wrapper.full-width {
  width: 100%;
  height: auto;
}

.image-upload-box {
  position: relative;
  width: 100%;
  height: 134px;
  border: 1px dashed var(--v-primary-base);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
}

.image-upload-box.has-image {
  border: none;
}

.product-image {
  border-radius: 8px;
}

.image-upload-box input[type='file'] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.image-upload-box.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

::v-deep .theme--dark .sticky-product-fields,
::v-deep .theme--dark .sticky-footer {
  background-color: #1e1e1e;
}

::v-deep .theme--dark .sticky-footer {
  border-top-color: rgba(255, 255, 255, 0.12);
}
</style>
